import { FC, useCallback, useMemo } from "react";
import {
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { updateSharedCost } from "@/store/dimensions/dimensionsSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DistributionType } from "@/services/cloudchipr.api";
import { dimensionCategoriesIdsWithNamesSelector } from "@/store/dimensions/selectors/slice-data/categories/dimensionCategoriesIdsWithNamesSelector";
import { generateShareAmountsForSharedCost } from "@/components/pages/dimensions/utils/helpers/shared-cost/generateShareAmountsForSharedCost";

interface DimensionCostAllocationCardAllocationTypeSelectProps {
  sharedCostId: string;
  distributionType: DistributionType;
}

export const DimensionCostAllocationCardAllocationTypeSelect: FC<
  DimensionCostAllocationCardAllocationTypeSelectProps
> = ({ sharedCostId, distributionType }) => {
  const dispatch = useAppDispatch();

  const categoryNamesWithIds = useAppSelector(
    dimensionCategoriesIdsWithNamesSelector,
  );

  const manuallySplitData = useMemo(() => {
    return generateShareAmountsForSharedCost(Object.keys(categoryNamesWithIds));
  }, [categoryNamesWithIds]);

  const renderValue = useCallback((value: DistributionType) => {
    return labels[value];
  }, []);

  const changeHandler = useCallback(
    (event: SelectChangeEvent<DistributionType>) => {
      const value = event.target.value as DistributionType;

      dispatch(
        updateSharedCost({
          id: sharedCostId,
          distribution_type: value,
          share_amounts: value === "manual" ? manuallySplitData : undefined,
        }),
      );
    },
    [sharedCostId, manuallySplitData, dispatch],
  );

  return (
    <FormControl size="xsmall" sx={{ width: 150 }}>
      <Select
        value={distributionType}
        renderValue={renderValue}
        onChange={changeHandler}
      >
        <MenuItem value="manual">
          <ListItemText
            primary={labels.manual}
            secondary={
              <Typography variant="caption" color="text.secondary">
                Manually assign a percentage of the shared cost to each category
                in the Dimension.
              </Typography>
            }
          />
        </MenuItem>

        <MenuItem value="even">
          <ListItemText
            primary={labels.even}
            secondary={
              <Typography variant="caption" color="text.secondary">
                Automatically distribute the shared cost equally across all
                categories in the Dimension.
              </Typography>
            }
          />
        </MenuItem>
      </Select>
    </FormControl>
  );
};

const labels: Record<DistributionType, string> = {
  manual: "Manual Allocation",
  even: "Even Split",
};
